// ExamPage.jsx (refactored main component)
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Sun, Moon, Clock, Target, Languages, Award, Gauge, CheckCircle, XCircle, Zap } from "lucide-react";
import examConfig from './config/examConfig';
import './ExamPage.css';
import Sanscript from "sanscript";
import { motion } from 'framer-motion';
import { krutidevToUnicode } from '../src/utils/krutidevConvertor';
import './styles/typingCommon.css';

import EnglishTyping from './components/EnglishTyping';
import HindiPhoneticTyping from './components/HindiPhoneticTyping';
import HindiKrutidevTyping from './components/HindiKrutidevTyping';

export default function ExamPage() {
  const { examId } = useParams();
  const navigate = useNavigate();
  const [keyCount, setKeyCount] = useState(0);
  const [user, setUser] = useState(null);
  const [theme, setTheme] = useState(() => {
    return localStorage.getItem("typeace-theme") || "light";
  });
  const config = examConfig[examId];
  const [selectedParagraph, setSelectedParagraph] = useState("");
  const [timeLeft, setTimeLeft] = useState(config?.duration * 60 || 0);
  const [started, setStarted] = useState(false);
  const [finished, setFinished] = useState(false);
  const [input, setInput] = useState("");
  const [wpm, setWpm] = useState(0);
  const [grossWpm, setGrossWpm] = useState(0);
  const [netWpm, setNetWpm] = useState(0);
  const [accuracy, setAccuracy] = useState(100);
  const [errors, setErrors] = useState(0);
  const [correctChars, setCorrectChars] = useState(0);
  const [totalChars, setTotalChars] = useState(0);
  const textareaRef = useRef(null);
  const timerRef = useRef(null);
  const paragraphRef = useRef(null);

  // Get random paragraph ensuring it's different from current
  const getRandomParagraph = () => {
    if (!config?.paragraphs?.length) return "";
    let randomIndex;
    do {
      randomIndex = Math.floor(Math.random() * config.paragraphs.length);
    } while (config.paragraphs[randomIndex] === selectedParagraph);

    return config.paragraphs[randomIndex];
  };

  const themes = ["light", "dark", "neon"];
  const cycleTheme = () => {
    const currentIndex = themes.indexOf(theme);
    const nextIndex = (currentIndex + 1) % themes.length;
    setTheme(themes[nextIndex]);
  };

  // Load user data
  useEffect(() => {
    const storedUser = localStorage.getItem("exam_registration");
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
  }, []);
  
  // Redirect if no user
  useEffect(() => {
    const user = localStorage.getItem("exam_registration");
    if (!user) {
      navigate(`/test-setup/${examId}`);
    }
  }, [examId, navigate]);

  // Set random paragraph
  useEffect(() => {
    if (config?.paragraphs?.length) {
      setSelectedParagraph(getRandomParagraph());
    }
  }, [config]);

  // Apply theme
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    localStorage.setItem("typeace-theme", theme);
  }, [theme]);

  // Failsafe redirect
  useEffect(() => {
    if (!config) {
      navigate('/form');
    }
  }, [config, navigate]);

  // Set timer duration
  useEffect(() => {
    if (config) {
      setTimeLeft(config.duration * 60);
    }
  }, [config]);

  // Timer logic
  useEffect(() => {
    if (started && timeLeft > 0 && !finished) {
      timerRef.current = setTimeout(() => {
        setTimeLeft(prev => prev - 1);
      }, 1000);
    } else if (timeLeft === 0 && started) {
      handleTestComplete();
    }
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [started, timeLeft, finished]);

  const calculateMetrics = useCallback((value) => {
    if (!config) return;
    let correct = 0;
    let total = 0;
    let normalizedInput = value;
    let normalizedPara = selectedParagraph;
    // ✅ FIX: convert ONLY input (not paragraph)
    if (config.language === "HI_KRUTIDEV") {
      try {
        normalizedInput = krutidevToUnicode(value);
        normalizedPara = selectedParagraph;
      } catch {
        normalizedInput = value;
        normalizedPara = selectedParagraph;
      }
    }
    // ✅ NEW: PHONETIC SUPPORT
    if (config.language === "HI_PHONETIC") {
      try {
        normalizedInput = Sanscript.t(input, "hk", "devanagari");
      } catch {
        normalizedInput = input;
      }
    }
    const inputChars = Array.from(normalizedInput);
    const paraChars = Array.from(normalizedPara);
    for (let i = 0; i < inputChars.length; i++) {
      if (i < paraChars.length) {
        total++;
        if (inputChars[i] === paraChars[i]) {
          correct++;
        }
      }
    }
    setCorrectChars(correct);
    setTotalChars(total);
    const acc = total === 0 ? 100 : (correct / total) * 100;
    setAccuracy(Number(acc.toFixed(1)));
    const errorCount = total - correct;
    setErrors(errorCount);
    const minutes = (config.duration * 60 - timeLeft) / 60;
    if (minutes > 0) {
      const grossWords = normalizedInput.length / 5;
      const grossSpeed = grossWords / minutes;
      const penaltyWords = errorCount / 5;
      const netSpeed = (grossWords - penaltyWords) / minutes;
      setGrossWpm(Math.max(0, Math.round(grossSpeed)));
      setNetWpm(Math.max(0, Math.round(netSpeed)));
      setWpm(Math.max(0, Math.round(netSpeed)));
    }
  }, [config, timeLeft, selectedParagraph]);

  // Handle test completion (UNCHANGED)
  const handleTestComplete = () => {
    setFinished(true);
    setStarted(false);
    const errorPercentage = totalChars === 0 ? 0 : (errors / totalChars) * 100;
    const passed =
      netWpm >= config.minWPM &&
      accuracy >= config.minAccuracy &&
      errorPercentage <= 5;
    const student = JSON.parse(localStorage.getItem("exam_registration"));
    
    fetch(`${process.env.REACT_APP_API_URL}/submit-result`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        name: student.name,
        rollNo: student.rollNo,
        email: student.email,
        institutionId: student.institutionId,
        examId: config.examId,
        grossWpm,
        netWpm,
        accuracy,
        errors,
        correctChars,
        totalChars,
        keyDepressions: keyCount,
        paragraph: selectedParagraph,
        typedText: input,
        passed
      })
    })
    .then(res => res.json())
    .then(data => {
      alert("Test submitted successfully");
    })
    .catch(err => {
      console.log("Error saving result:", err);
    });
    
    const history = JSON.parse(localStorage.getItem("typeace-history")) || [];
    history.push({
      exam: config.examName,
      date: new Date().toLocaleString(),
      grossWpm,
      netWpm,
      accuracy,
      errors,
      passed
    });
    localStorage.setItem("typeace-history", JSON.stringify(history));
  };

  // Handle restart (UNCHANGED)
  const handleRestart = () => {
    setTimeLeft(config.duration * 60);
    setStarted(false);
    setFinished(false);
    setInput("");
    setWpm(0);
    setGrossWpm(0);
    setNetWpm(0);
    setAccuracy(100);
    setErrors(0);
    setCorrectChars(0);
    setTotalChars(0);
    if (config?.paragraphs?.length) {
      setSelectedParagraph(getRandomParagraph());
    }
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  };

  // Format time display (UNCHANGED)
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Get timer color based on remaining time (UNCHANGED)
  const getTimeColor = () => {
    const percentage = (timeLeft / (config.duration * 60)) * 100;
    if (percentage > 50) return 'var(--text)';
    if (percentage > 20) return '#e67e22';
    return '#c0392b';
  };

  // Render appropriate typing component based on language
  const renderTypingComponent = () => {
    const commonProps = {
      input,
      setInput,
      started,
      setStarted,
      selectedParagraph,
      calculateMetrics,
      handleTestComplete,
      setKeyCount,
      textareaRef,
      paragraphRef
    };
    switch (config?.language) {
      case "EN":
        return <EnglishTyping {...commonProps} />;
      case "HI_PHONETIC":
        return <HindiPhoneticTyping {...commonProps} />;
      case "HI_KRUTIDEV":
        return <HindiKrutidevTyping {...commonProps} />;
      default:
        return null;
    }
  };

  if (!config || !selectedParagraph) return null;
  let normalizedInput = input;
  let normalizedPara = selectedParagraph;

  if (config.language === "HI_KRUTIDEV") {
    try {
      normalizedInput = krutidevToUnicode(input);
      normalizedPara = selectedParagraph; // ✅ FIXED
    } catch {
      normalizedInput = input;
      normalizedPara = selectedParagraph;
    }
  }

  const inputChars = Array.from(normalizedInput);
  const paraChars = Array.from(normalizedPara);

  // Helper for Hindi preview
  const getHindiPreview = (text) => {
    try {
      return krutidevToUnicode(text);
    } catch {
      return text;
    }
  };
  const hindiPreview = getHindiPreview(input);

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -12 }}
      transition={{
        duration: 0.3,
        ease: "easeInOut"
      }}
    >
      <div className={`exam-page 
        ${config.language.startsWith("HI") ? 'hindi-mode' : ''} 
        ${config.language === "HI_KRUTIDEV" ? 'kruti' : ''}
      `}>
        <div className="exam-container">
          {/* Header */}
          <header className="exam-header">
            <div className="header-left">
              <button onClick={() => navigate('/form')} className="back-btn">
                <ArrowLeft size={18} />
                <span>Back to Modes</span>
              </button>
            </div>
            
            <div className="header-center">
              <h1>{config.examName}</h1>
              <span className="exam-code-badge">{config.examId}</span>
            </div>
            
            <button onClick={cycleTheme} className="theme-toggle">
              {theme === "light" && <Sun size={18} />}
              {theme === "dark" && <Moon size={18} />}
              {theme === "neon" && <Zap size={18} />}
            </button>
          </header>

          {/* Exam Info Card */}
          <div className="info-card">
            <div className="info-grid">
              <div className="info-item">
                <Clock size={18} />
                <div className="info-content">
                  <span className="info-label">Duration</span>
                  <span className="info-value">{config.duration} minutes</span>
                </div>
              </div>
              
              <div className="info-item">
                <Target size={18} />
                <div className="info-content">
                  <span className="info-label">Required WPM</span>
                  <span className="info-value">{config.minWPM} WPM</span>
                </div>
              </div>
              
              <div className="info-item">
                <Gauge size={18} />
                <div className="info-content">
                  <span className="info-label">Min Accuracy</span>
                  <span className="info-value">{config.minAccuracy}%</span>
                </div>
              </div>
              
              <div className="info-item">
                <Languages size={18} />
                <div className="info-content">
                  <span className="info-label">Language</span>
                  <span className="info-value">{config.language}</span>
                </div>
              </div>
              
              <div className="info-item">
                <Award size={18} />
                <div className="info-content">
                  <span className="info-label">Total Marks</span>
                  <span className="info-value">{config.totalMarks}</span>
                </div>
              </div>
              
              {config.minKeyDepressions && (
                <div className="info-item">
                  <Target size={18} />
                  <div className="info-content">
                    <span className="info-label">Key Depressions</span>
                    <span className="info-value">{config.minKeyDepressions}</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Typing Section */}
          {!finished ? (
            <>
              <div className="timer-display" style={{ color: getTimeColor() }}>
                {formatTime(timeLeft)}
              </div>

              <div className="paragraph-box" ref={paragraphRef}>
                
                <p>
                  {paraChars.map((char, index) => {
                    let className = "";

                    if (index < inputChars.length) {
                      className =
                        inputChars[index] === char
                          ? "correct-char"
                          : "wrong-char";
                    }

                    return (
                      <span key={index} className={className}>
                        {char}
                      </span>
                    );
                  })}
                </p>
              </div>

              {/* Render language-specific typing component */}
              {renderTypingComponent()}
              {config.language === "HI_PHONETIC" && (
                <div
                  style={{
                    marginTop: "10px",
                    padding: "12px",
                    background: "var(--bg)",
                    border: "1px solid var(--border)",
                    borderRadius: "8px",
                    fontSize: "18px",
                    fontFamily: "'Noto Sans Devanagari', sans-serif",
                    color: "var(--text)"
                  }}
                >
                  {input ? Sanscript.t(input, "hk", "devanagari") : "Type to see Hindi..."}
                </div>
              )}
              <div className="live-stats">
                <div className="stat-block">
                  <span className="stat-block-label">WPM</span>
                  <span className="stat-block-value">{wpm}</span>
                </div>
                
                <div className="stat-block">
                  <span className="stat-block-label">Accuracy</span>
                  <span className="stat-block-value">{accuracy}%</span>
                </div>
                
                <div className="stat-block">
                  <span className="stat-block-label">Errors</span>
                  <span className="stat-block-value">{errors}</span>
                </div>
                
                <div className="stat-block">
                  <span className="stat-block-label">Time Left</span>
                  <span className="stat-block-value">{formatTime(timeLeft)}</span>
                </div>
              </div>
            </>
          ) : (
            <div className="result-card">
              <h2>Test Submitted Successfully</h2>

              <p style={{textAlign:"center", marginTop:"10px"}}>
                Your typing test has been submitted successfully.
              </p>

              <div className="result-paragraphs">
                <div className="original-para">
                  <h3>Paragraph Given</h3>
                  <p>{selectedParagraph}</p>
                </div>

                <div className="typed-para">
                  <h3>Your Typed Text</h3>
                  <p>{input}</p>
                </div>
              </div>

              <div className="result-actions">
                <button onClick={() => navigate('/form')} className="back-modes-btn">
                  Back to Modes
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
}